import { Link, useParams, Navigate } from "react-router-dom";
import { CalendarDays, Clock, ChevronRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { CtaBand, Breadcrumbs } from "@/components/Blocks";
import {
  INSIGHTS, SERVICES, SITE_URL, PHONE, EMAIL, getInsight, breadcrumbLd,
} from "@/data/contentEngine";

const InsightArticle = () => {
  const { slug } = useParams();
  const article = getInsight(slug);
  if (!article) return <Navigate to="/insights" replace />;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: article.title,
        description: article.excerpt,
        datePublished: article.date,
        author: {
          "@type": "Organization",
          name: "Xtreme Protection — Northern Ireland Security Services",
          url: SITE_URL,
        },
        publisher: {
          "@type": "Organization",
          name: "Xtreme Protection — Northern Ireland Security Services",
          url: SITE_URL,
        },
      },
      breadcrumbLd([
        { label: "Home", to: "/" },
        { label: "Insights", to: "/insights" },
        { label: article.title, to: `/insights/${article.slug}` },
      ]),
    ],
  };

  const related = INSIGHTS.filter((a) => a.slug !== article.slug).slice(0, 3);

  return (
    <>
      <Seo
        title={`${article.title} | Xtreme Protection NI`}
        description={article.excerpt}
        path={`/insights/${article.slug}`}
        jsonLd={jsonLd}
      />
      <article className="mx-auto max-w-4xl px-4 pb-16 pt-14 sm:px-6 sm:pt-20 lg:px-12" data-testid="article-content">
        <Breadcrumbs
          items={[
            { label: "Home", to: "/" },
            { label: "Insights", to: "/insights" },
            { label: article.category, to: null },
          ]}
        />
        <Reveal>
          <span className="mt-8 inline-block rounded-sm border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-amber-400">
            {article.category}
          </span>
          <h1
            className="mt-4 font-display text-3xl font-black uppercase leading-[1.08] tracking-tight text-slate-50 sm:text-5xl"
            data-testid="article-title"
          >
            {article.title}
          </h1>
          <div className="mt-5 flex items-center gap-4 font-mono text-[11px] uppercase tracking-wider text-slate-500">
            <span className="flex items-center gap-1.5">
              <CalendarDays className="h-3.5 w-3.5" />
              {new Date(article.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" /> {article.readTime}
            </span>
          </div>
          <p className="mt-6 border-l-2 border-amber-500 pl-4 text-base leading-relaxed text-slate-300 sm:text-lg">
            {article.excerpt}
          </p>
        </Reveal>
        <div className="mt-10 space-y-10">
          {article.sections.map((s, i) => (
            <Reveal key={i} delay={0.05}>
              <section data-testid={`article-section-${i}`}>
                <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-slate-100">
                  {s.heading}
                </h2>
                <div className="mt-4 space-y-4">
                  {s.paras.map((p, j) => (
                    <p key={j} className="text-base leading-relaxed text-slate-400">
                      {p}
                    </p>
                  ))}
                </div>
              </section>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.08}>
          <div className="mt-14 rounded-md border border-slate-800 bg-obsidian-900 p-6" data-testid="article-services-panel">
            <h2 className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-amber-400">
              Related security services
            </h2>
            <ul className="mt-4 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`article-service-${s.slug}`}
                    className="group flex items-center justify-between rounded-sm px-2 py-1.5 text-sm text-slate-300 transition-colors hover:bg-obsidian-800 hover:text-amber-400"
                  >
                    {s.title} Northern Ireland
                    <ChevronRight className="h-3.5 w-3.5 text-slate-700 group-hover:text-amber-500" />
                  </Link>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-sm text-slate-400">
              Questions about your own premises? Call{" "}
              <a href="tel:+442046402840" className="text-amber-400 hover:underline" data-testid="article-phone-link">{PHONE}</a>{" "}
              or email{" "}
              <a href="mailto:admin@xtremeprotection.uk" className="text-amber-400 hover:underline" data-testid="article-email-link">{EMAIL}</a>.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <h2 className="mt-14 font-display text-2xl font-bold uppercase tracking-tight text-slate-100" data-testid="article-related-heading">
            Keep reading
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/insights/${r.slug}`}
                data-testid={`article-related-${r.slug}`}
                className="tactical-card group flex h-full flex-col rounded-md p-5"
              >
                <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-amber-400">
                  {r.category}
                </span>
                <span className="mt-2 flex-1 font-display text-base font-bold uppercase leading-snug text-slate-100">
                  {r.title}
                </span>
                <ChevronRight className="mt-3 h-4 w-4 text-slate-600 transition-all duration-300 group-hover:translate-x-1 group-hover:text-amber-500" />
              </Link>
            ))}
          </div>
        </Reveal>
      </article>
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-12">
        <CtaBand
          title="Put this advice to work"
          sub="Free risk assessment for any premises in Northern Ireland — speak directly to our control room."
        />
      </section>
    </>
  );
};

export default InsightArticle;
